// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { colors } from '@toggl/style'
import { Track, text } from '@toggl/ui'

import { DayCard } from './DayCard'
import { ErrorRow } from './ErrorRow'
import {
  DAYS,
  ENTRIES,
  ENTRY_LABELS,
  ERROR_MESSAGES,
  MOBILE_VIEW_WIDTH,
  getFormattedTime,
} from './utils'
import type { TimeCardState } from './utils'

type Props = {|
  +timeCard: TimeCardState,
|}

export const TimeTable = ({ timeCard }: Props) => (
  <React.Fragment>
    <p className="no-print">Enter your time in 24-hour format.</p>
    <Container>
      <HeaderRow>
        <div className="label">Day</div>
        {ENTRIES.map((entryName) => (
          <div key={entryName}>{ENTRY_LABELS[entryName]}</div>
        ))}
        <div className="summary">Total</div>
      </HeaderRow>
      {DAYS.map((day) => (
        <React.Fragment key={day}>
          <Row>
            <DayCard day={day} dayCard={timeCard[day]} />
          </Row>
          <ErrorRow visible={!!timeCard[day].errorCode}>
            {timeCard[day].errorCode !== null &&
              ERROR_MESSAGES[timeCard[day].errorCode]}
          </ErrorRow>
        </React.Fragment>
      ))}
      <FooterRow>
        <div className="label">
          <strong>Total Hours</strong>
        </div>
        <div className="summary">{getFormattedTime(timeCard.total)}</div>
      </FooterRow>
    </Container>
    <UseTogglTrack className="force-print" href="https://toggl.com/track/">
      <text.P3 color={colors.fadedPurple}>
        Use Toggl Track, the free alternative to manual time cards
      </text.P3>
      <Track />
    </UseTogglTrack>
  </React.Fragment>
)

const Container: React.ComponentType<React.ElementConfig<'div'>> = styled.div`
  display: flex;
  flex-flow: column;
  gap: 30px;
  ${text.paragraph3};
`

const Row: React.ComponentType<React.ElementConfig<'div'>> = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;

  > * {
    flex: 2;
    text-align: center;

    :first-of-type {
      text-align: left;
    }

    :last-of-type {
      flex: 1;
      text-align: right;
    }
  }

  @media screen and (max-width: ${MOBILE_VIEW_WIDTH}) {
    flex-flow: column;
    align-items: stretch;
    gap: 5px;

    > *:first-of-type {
      text-align: center;
    }
  }
`

const HeaderRow: React.ComponentType<React.ElementConfig<'div'>> = styled(Row)`
  padding-bottom: 20px;
  border-bottom: 1px solid;
  text-transform: uppercase;
  font-weight: bold;
  @media screen and (max-width: ${MOBILE_VIEW_WIDTH}) {
    display: none;
  }
`

const FooterRow: React.ComponentType<React.ElementConfig<'div'>> = styled.div`
  padding-top: 20px;
  border-top: 1px solid;
  display: flex;
  justify-content: space-between;
  text-transform: uppercase;
`

const UseTogglTrack: React.ComponentType<
  React.ElementConfig<'div'>
> = styled.div`
  text-align: center;
  display: none;
  p {
    margin: 80px 0 20px;
  }
  svg {
    max-width: 200px;
  }
`
